"use client";

import dayjs from "dayjs";
import { useCallback, useMemo } from "react";
import { useQueryStates } from "nuqs";
import { spaParsers } from "./shared/parsers";
import { getStableTodayIso } from "@/lib/date";
import { buildQueryString } from "@/lib/utils";

export const useSpaQuery = () => {
  const [q, setQ] = useQueryStates(spaParsers, { history: "replace" });

  const setLocationId = useCallback(
    (value: string) =>
      setQ({ locationId: value.trim() || null, lat: null, lng: null }),
    [setQ],
  );

  const setCoordinates = useCallback(
    (lat: number, lng: number) =>
      setQ({ locationId: null, lat: `${lat}`, lng: `${lng}` }),
    [setQ],
  );

  const setSearch = useCallback(
    (value: string) => setQ({ search: value.trim() || null }),
    [setQ],
  );

  const setDate = useCallback(
    (date?: Date) => {
      const formatted = date ? dayjs(date).format("YYYY-MM-DD") : null;
      setQ({ date: formatted, start: formatted });
    },
    [setQ],
  );

  const setFilters = useCallback(
    (updates: Parameters<typeof setQ>[0]) => {
      if (!updates || typeof updates === "function") {
        setQ(updates);
        return;
      }
      const cleaned = Object.fromEntries(
        Object.entries(updates as Record<string, unknown>).map(
          ([key, value]) => [
            key,
            Array.isArray(value) && value.length === 0 ? null : value,
          ],
        ),
      ) as Parameters<typeof setQ>[0];
      setQ(cleaned);
    },
    [setQ],
  );

  const clearSearch = useCallback(
    () =>
      setQ({
        locationId: null,
        search: null,
        lat: null,
        lng: null,
        date: null,
        start: null,
      }),
    [setQ],
  );

  const clearFilters = useCallback(
    () =>
      setQ({
        priceRange: null,
        durationRange: null,
        serviceType: null,
        rating: null,
      }),
    [setQ],
  );

  const defaultToday = getStableTodayIso();

  return {
    locationId: q.locationId ?? "",
    search: q.search ?? "",
    lat: q.lat ?? "",
    lng: q.lng ?? "",
    date: q.date ?? q.start ?? defaultToday,

    priceRange: q.priceRange ?? [],
    durationRange: q.durationRange ?? [],
    serviceType: q.serviceType ?? [],
    rating: q.rating ?? [],

    setLocationId,
    setSearch,
    setCoordinates,
    setDate,
    clearSearch,
    setFilters,
    clearFilters,
  };
};

interface SpaMassage {
  id: string | number;
  title?: string;
  name?: string;
  provinceId?: string | number;
}

export function useSpaHref(spa: SpaMassage, href?: string): string {
  const { date, lat, lng } = useSpaQuery();

  return useMemo(() => {
    if (href) return href;

    const query = buildQueryString({
      search: spa.title ?? spa.name,
      start: date,
      locationId: spa.provinceId,
      lat: lat && lng ? lat : undefined,
      lng: lat && lng ? lng : undefined,
    });

    return `/spa-msg/${spa.id}${query ? `?${query}` : ""}`;
  }, [href, spa.id, spa.title, spa.name, spa.provinceId, date, lat, lng]);
}
